import { CURRENCIES, CURRENCY_INFO, formatMoney, type Category, type CurrencyCode } from '@expenditure/core';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, type IconName } from './Icon';

// ---------------------------------------------------------------------------
// Money
// ---------------------------------------------------------------------------

interface MoneyProps {
  minor: number;
  currency: CurrencyCode;
  /** Colour positive green / negative red. */
  tone?: boolean;
  signed?: boolean;
  /** Show the absolute value (sign conveyed by wording instead). */
  absolute?: boolean;
  className?: string;
}

export function Money({ minor, currency, tone, signed, absolute, className = '' }: MoneyProps) {
  const value = absolute ? Math.abs(minor) : minor;
  const toneClass = tone ? (minor > 0 ? 'pos' : minor < 0 ? 'neg' : 'muted') : '';
  return (
    <span className={`num ${toneClass} ${className}`}>{formatMoney(value, currency, { signed })}</span>
  );
}

// ---------------------------------------------------------------------------
// Avatar
// ---------------------------------------------------------------------------

const AVATAR_COLORS = ['#0b7a5c', '#0a6b8a', '#6a4fc9', '#c2410c', '#b83280', '#2f6f3e', '#9a6b00', '#3949ab'];

function colorFor(seed: string): string {
  let hash = 0;
  for (const char of seed) hash = (hash * 31 + char.charCodeAt(0)) | 0;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]!;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const letters = parts.length > 1 ? [parts[0]!, parts[parts.length - 1]!] : parts.slice(0, 1);
  return letters.map((p) => Array.from(p)[0] ?? '').join('').toUpperCase() || '?';
}

export function Avatar({ name, photo, small }: { name: string; photo?: string | null; small?: boolean }) {
  const className = `avatar ${small ? 'avatar--sm' : ''}`;
  if (photo) return <img className={className} src={photo} alt="" loading="lazy" referrerPolicy="no-referrer" />;
  return (
    <span className={className} style={{ background: colorFor(name) }} aria-hidden>
      {initials(name)}
    </span>
  );
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** Eases a number from its previous value to `target` (whole minor units, so money stays exact at rest). */
export function useCountUp(target: number, durationMs = 700): number {
  const [value, setValue] = useState(target);
  const from = useRef(target);
  const isFirst = useRef(true);
  useEffect(() => {
    // First paint counts up from zero; later changes animate from the old value.
    const start = isFirst.current ? 0 : from.current;
    isFirst.current = false;
    if (start === target || prefersReducedMotion()) {
      from.current = target;
      setValue(target);
      return;
    }
    let frame = 0;
    const began = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - began) / durationMs);
      const eased = 1 - (1 - p) ** 3;
      setValue(Math.round(start + (target - start) * eased));
      if (p < 1) frame = requestAnimationFrame(tick);
      else from.current = target;
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, durationMs]);
  return value;
}

/** A large balance with quieter cents ("$7,854" + ".43") that counts up when it changes. */
export function HeroAmount({ minor, currency, signed }: { minor: number; currency: CurrencyCode; signed?: boolean }) {
  const shown = useCountUp(minor);
  const text = formatMoney(shown, currency, { signed });
  const dot = currency === 'USD' ? text.lastIndexOf('.') : -1;
  return (
    <div className="hero-amount num" aria-label={formatMoney(minor, currency, { signed })}>
      <span aria-hidden>
        {dot === -1 ? (
          text
        ) : (
          <>
            {text.slice(0, dot)}
            <span className="cents">{text.slice(dot)}</span>
          </>
        )}
      </span>
    </div>
  );
}

/** Category glyph on a tile tinted with the category's colour. */
export function CategoryIcon({ category, small }: { category: Category; small?: boolean }) {
  return (
    <span className={`icon-tile cat cat--${category} ${small ? 'icon-tile--sm' : ''}`} aria-hidden>
      <Icon name={category} size={small ? 16 : 20} />
    </span>
  );
}

// ---------------------------------------------------------------------------
// Layout helpers
// ---------------------------------------------------------------------------

export function PageHeader({ title, action }: { title: ReactNode; action?: ReactNode }) {
  return (
    <header className="page-header">
      <h1 className="page-title">{title}</h1>
      {action}
    </header>
  );
}

export function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="section-title">
      <span>{children}</span>
      {action}
    </div>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
}) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {error ? <span className="field-error">{error}</span> : hint ? <span className="field-hint">{hint}</span> : null}
    </label>
  );
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: Array<{ value: T; label: ReactNode }>;
  value: T;
  onChange: (value: T) => void;
  label?: string;
}) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// States
// ---------------------------------------------------------------------------

export function Spinner() {
  return <div className="spinner" role="status" aria-label="Loading" />;
}

export function LoadingScreen() {
  return (
    <div className="center-screen">
      <Spinner />
    </div>
  );
}

export function PageSkeleton() {
  return (
    <div className="page">
      <div className="skeleton" style={{ height: 40, width: '50%' }} />
      <div className="skeleton" style={{ height: 150 }} />
      <div className="skeleton" style={{ height: 64 }} />
      <div className="skeleton" style={{ height: 220 }} />
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon: IconName;
  title: ReactNode;
  body?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <div className="empty-icon" aria-hidden>
        <Icon name={icon} size={28} />
      </div>
      <h3>{title}</h3>
      {body && <p>{body}</p>}
      {action}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="empty">
      <div className="empty-icon empty-icon--alert" aria-hidden>
        <Icon name="alert" size={28} />
      </div>
      <p>{message}</p>
      {onRetry && (
        <button className="btn btn--soft" onClick={onRetry}>
          {t('common.retry')}
        </button>
      )}
    </div>
  );
}

/** A native picker with every supported currency: "USD · US dollar ($)". */
export function CurrencySelect({
  value,
  onChange,
  label,
  compact,
}: {
  value: CurrencyCode;
  onChange: (currency: CurrencyCode) => void;
  label: string;
  /** Just the code, for tight spots (next to the amount). */
  compact?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <select
      className={`input currency-select ${compact ? 'currency-select--compact' : ''}`}
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value as CurrencyCode)}
    >
      {CURRENCIES.map((code) => (
        <option key={code} value={code}>
          {compact ? code : `${code} · ${t(`currencies.${code}`)} (${CURRENCY_INFO[code].symbol})`}
        </option>
      ))}
    </select>
  );
}
